import { Address } from "./address";
import { Customer } from "./customer";
import { Equipment } from "./equipment";
import EquipmentType from "./equipmentType";
import { Note } from "./note";
import { Transaction } from "./transaction";

export class Invoice {
  invoice_id = -1;
  appointment_id = -1;
  job_id = -1;
  quote_type = "";
  summary?: string;
  show_summary?: boolean;
  format?: string;
  customer = new Customer();
  billing_customer = new Customer();
  service_address?: Address;
  billing_address?: Address;
  links?: {
    invoice_approve: string;
    invoice_email: string;
    invoice_pdf: string;
    invoice_refund: string;
    payment_create: string;
    payment_list: string;
    payment_receivers: string;
    equipment_link_create: string;
    update_details: string;
    invoice_product_usage: string;
    edit_invoice_date: string;
    invoice_break: string;
    void: string;
    update_batching: string;
    resubmit: string;
    update_display_membership_savings: string;
    // Present only when a pay-by-SMS link may be sent (feature on, processor
    // configured, payment rights, payable). revoke_pay_link is present only
    // while a link is active. (TB-1884)
    payby?: string;
    revoke_pay_link?: string;
  } = undefined;
  signature?: {
    kind: string;
    image_url: string;
    signature_time: string;
  } = undefined;
  items_attributes: InvoiceLineItem[] = [];
  financials = {
    subtotal_cents: -1,
    discount_amount_cents: 0,
    refundable_amount_cents: 0,
    late_fee_cents: 0,
    sales_tax_cents: 0,
    total_cents: 0,
    total_transactions_cents: 0,
    balance_cents: 0,
    savings_cents: undefined as number | undefined
  };
  notes: Note[] = [];
  finalized: false;
  voided: false;
  invoice_date = ""; // Date
  next_payment_key = "";
  payments: Transaction[] = [];
  payment_create_url?: string;
  payment_list_url?: string;
  payment_receivers_url?: string;
  pdf_url?: string;
  display_membership_savings?: boolean;
  exclude_from_batching: boolean;
  accounting_class_id?: number | null;
  discount_line_item_accounting_classes?: boolean;
  original_billing_customer?: Customer;
  inferred_billing_customer?: Customer;
  qbo_sync_status?: string;
  qbo_id?: string;
  qbo_url?: string;
  progress_billing?: ProgressBillingPayload;
  constructor(params: Partial<Invoice> = {}) {
    Object.assign(this, params);
  }
}

export type SpbInstallmentStatus =
  | "paid"
  | "partially_paid"
  | "current_draw"
  | "past_due"
  | "scheduled"
  | "cancelled";

export type SpbPaymentStatus =
  | "voided"
  | "paid_in_full"
  | "past_due"
  | "current_draw_due"
  | "no_payment_due";

export interface ProgressBillingInstallment {
  id: number;
  position: number;
  label: string;
  amount_cents: number;
  paid_cents: number;
  remaining_cents: number;
  percentage: number;
  due_date: string | null;
  status: SpbInstallmentStatus;
  collectible: boolean;
}

export interface ProgressBillingCurrentDraw {
  label: string;
  // amount_cents = full draw value (per the schedule); remaining_cents =
  // unpaid portion after the pool walk. Both exposed because consumers
  // disagree: the Accept Payment modal pre-populates with remaining_cents,
  // the schedule table renders the full amount_cents column.
  amount_cents: number;
  remaining_cents: number;
  due_date: string | null;
  is_due: boolean;
}

export interface ProgressBillingNextDraw {
  label: string;
  due_date: string | null;
}

export interface ProgressBillingPayload {
  is_progress_billing: true;
  payment_status: SpbPaymentStatus;
  past_due: boolean;
  past_due_amount_cents: number;
  // Top-level totals — consumed by the Accept Payment modal's summary box
  // (contract total / paid to date / balance remaining). Distinct from
  // financials.* on the standard invoice payload because these cents are
  // SPB-specific (e.g., balance_cents on financials does not net per-draw
  // refunds the same way).
  contract_total_cents: number;
  paid_to_date_cents: number;
  total_balance_remaining_cents: number;
  // True when line items changed since the plan was created (quote
  // added/removed). Decoupled from payment progress.
  plan_drift: boolean;
  plan_needs_review: boolean;
  current_draw: ProgressBillingCurrentDraw | null;
  next_draw_after_current: ProgressBillingNextDraw | null;
  installments: ProgressBillingInstallment[];
}

export class InvoiceLineItem {
  id = -1;
  item_id = -1;
  item_type = "";
  unit_name = "";
  description = "";
  warranty = "";
  quantity = 0;
  unit_price_cents = 0;
  total_cents = 0;
  comparison_price_cents?: number = undefined;
  comparison_total_cents = 0;
  covered_systems = 0;
  type = "";
  invoice_format?: string;
  accounting_class_id?: number | null;
  equipment_request_status = "";
  equipment_links: EquipmentLink[] = [];
  links?: {
    time_override: string;
    pricebook_link: string;
    equipment_link_create: string;
  } = undefined;
  items_in_group?: InvoiceLineItem[] = [];

  constructor(params: Partial<InvoiceLineItem> = {}) {
    Object.assign(this, params);
  }
}

export class EquipmentLink {
  id = -1;
  invoice_line_item_id = -1;
  equipment_type = new EquipmentType();
  equipment?: Equipment = undefined;
  not_applicable = false;
  status = "";
  optional = false;
  links?: {
    equipment_link_update: string;
    equipment_link_destroy: string;
  } = undefined;

  constructor(params: Partial<EquipmentLink> = {}) {
    Object.assign(this, params);
  }
}

export enum EquipmentRequestStatus {
  FULFILLED = "fulfilled",
  UNFULFILLED = "unfulfilled",
  OPTIONAL = "optional"
}

export class InventoryUsage {
  id = -1;
  part_id = -1;
  inventory_location_id: number | null = null;
  quantity = 0;
  invoice_line_item_id: number | null = null;
  quote_line_item_id: number | null = null;
  part?: {
    id: number;
    name: string;
    vendor_sku: string;
    details: string;
  } = undefined;

  constructor(params: Partial<InventoryUsage> = {}) {
    Object.assign(this, params);
  }
}
